import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaLeaf,
  FaCertificate,
  FaBarcode,
  FaDownload,
  FaBoxOpen,
  FaShieldAlt,
  FaSnowflake,
  FaUtensils,
  FaFire,
  FaDrumstickBite,
  FaFish,
  FaSeedling,
  FaRecycle,
  FaHeart,
  FaCarrot,
  FaHamburger,
  FaPizzaSlice,
  FaIcicles,
  FaTimes,
  FaQuestionCircle,
  FaChevronUp,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const JackfruitBananaBlossom = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Products array with translations
  const products = useMemo(
    () => [
      {
        name: t("JackfruitBananaBlossom.products.bananaBlossom.name"),
        nickname: t("JackfruitBananaBlossom.products.bananaBlossom.nickname"),
        images: [
          "/images/products/banana-blossom-pieces-pack.webp",
          "/images/products/banana-blossom-pieces-closeup.webp",
        ],
        image: "🌺",
        color: "from-yellow-300 to-amber-400",
        appearance: t(
          "JackfruitBananaBlossom.products.bananaBlossom.appearance"
        ),
        aroma: t("JackfruitBananaBlossom.products.bananaBlossom.aroma"),
        flavour: t("JackfruitBananaBlossom.products.bananaBlossom.flavour"),
        texture: t("JackfruitBananaBlossom.products.bananaBlossom.texture"),
        ingredients: t(
          "JackfruitBananaBlossom.products.bananaBlossom.ingredients"
        ),
        servingIdeas: t(
          "JackfruitBananaBlossom.products.bananaBlossom.servingIdeas",
          { returnObjects: true }
        ),
        dataSheet: "/images/DataSheet/banana-blossom-pieces.jpg",
      },
      {
        name: t("JackfruitBananaBlossom.products.bananaBlossomShredded.name"),
        nickname: t(
          "JackfruitBananaBlossom.products.bananaBlossomShredded.nickname"
        ),
        images: [
          "/images/products/banana-blossom-shredded-pack.webp",
          "/images/products/banana-blossom-shredded-texture.webp",
        ],
        image: "🌺",
        color: "from-yellow-200 to-yellow-400",
        appearance: t(
          "JackfruitBananaBlossom.products.bananaBlossomShredded.appearance"
        ),
        aroma: t("JackfruitBananaBlossom.products.bananaBlossomShredded.aroma"),
        flavour: t(
          "JackfruitBananaBlossom.products.bananaBlossomShredded.flavour"
        ),
        texture: t(
          "JackfruitBananaBlossom.products.bananaBlossomShredded.texture"
        ),
        ingredients: t(
          "JackfruitBananaBlossom.products.bananaBlossomShredded.ingredients"
        ),
        servingIdeas: t(
          "JackfruitBananaBlossom.products.bananaBlossomShredded.servingIdeas",
          { returnObjects: true }
        ),
        dataSheet: "/images/DataSheet/banana-blossom-shredded.jpg",
      },
      {
        name: t("JackfruitBananaBlossom.products.jackfruitPieces.name"),
        nickname: t("JackfruitBananaBlossom.products.jackfruitPieces.nickname"),
        images: [
          "/images/products/jackfruit-pieces-pack.webp",
          "/images/products/jackfruit-pieces-closeup.webp",
        ],
        image: "🍈",
        color: "from-green-200 to-lime-400",
        appearance: t(
          "JackfruitBananaBlossom.products.jackfruitPieces.appearance"
        ),
        aroma: t("JackfruitBananaBlossom.products.jackfruitPieces.aroma"),
        flavour: t("JackfruitBananaBlossom.products.jackfruitPieces.flavour"),
        texture: t("JackfruitBananaBlossom.products.jackfruitPieces.texture"),
        ingredients: t(
          "JackfruitBananaBlossom.products.jackfruitPieces.ingredients"
        ),
        servingIdeas: t(
          "JackfruitBananaBlossom.products.jackfruitPieces.servingIdeas",
          { returnObjects: true }
        ),
        dataSheet: "/images/DataSheet/jackfruit-pieces.jpg",
      },
      {
        name: t("JackfruitBananaBlossom.products.jackfruitPulled.name"),
        nickname: t("JackfruitBananaBlossom.products.jackfruitPulled.nickname"),
        images: [
          "/images/products/jackfruit-pulled-pack.webp",
          "/images/products/jackfruit-pulled-texture.webp",
        ],
        image: "🍖",
        color: "from-green-300 to-emerald-400",
        appearance: t(
          "JackfruitBananaBlossom.products.jackfruitPulled.appearance"
        ),
        aroma: t("JackfruitBananaBlossom.products.jackfruitPulled.aroma"),
        flavour: t("JackfruitBananaBlossom.products.jackfruitPulled.flavour"),
        texture: t("JackfruitBananaBlossom.products.jackfruitPulled.texture"),
        ingredients: t(
          "JackfruitBananaBlossom.products.jackfruitPulled.ingredients"
        ),
        servingIdeas: t(
          "JackfruitBananaBlossom.products.jackfruitPulled.servingIdeas",
          { returnObjects: true }
        ),
        dataSheet: "/images/DataSheet/jackfruit-pulled.jpg",
      },
    ],
    [t]
  );

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-green-600 to-lime-600 text-white p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t("JackfruitBananaBlossom.downloadModal.title")}
                </h3>
                <p className="text-green-100 text-sm">
                  {t("JackfruitBananaBlossom.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List - Scrollable */}
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-lime-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    {/* Product Icon */}
                    <div
                      className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-3xl shadow-md`}
                    >
                      {product.image}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                        {product.name}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        <span className="inline-flex items-center space-x-1">
                          <FaSeedling className="text-green-600" />
                          <span>{product.nickname}</span>
                        </span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t("JackfruitBananaBlossom.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-green-600 text-white rounded-full p-3 group-hover:bg-green-700 group-hover:scale-110 transition-all duration-300 shadow-md">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // FAQs with translations
  const faqs = useMemo(
    () => t("JackfruitBananaBlossom.faqs", { returnObjects: true }),
    [t]
  );

  // Certifications with translations
  const certifications = useMemo(
    () => [
      {
        name: t("JackfruitBananaBlossom.certifications.brc.name"),
        image: "/images/certifications/BRC.webp",
        alt: t("JackfruitBananaBlossom.certifications.brc.name"),
        description: t("JackfruitBananaBlossom.certifications.brc.description"),
        icon: "🏆",
      },
      {
        name: t("JackfruitBananaBlossom.certifications.eu.name"),
        image: "/images/certifications/EU.webp",
        alt: t("JackfruitBananaBlossom.certifications.eu.name"),
        description: t("JackfruitBananaBlossom.certifications.eu.description"),
        icon: "🇪🇺",
      },
      {
        name: t("JackfruitBananaBlossom.certifications.usda.name"),
        image: "/images/certifications/USDA.webp",
        alt: t("JackfruitBananaBlossom.certifications.usda.name"),
        description: t(
          "JackfruitBananaBlossom.certifications.usda.description"
        ),
        icon: "🇺🇸",
      },
      {
        name: t("JackfruitBananaBlossom.certifications.jas.name"),
        image: "/images/certifications/JAS.webp",
        alt: t("JackfruitBananaBlossom.certifications.jas.name"),
        description: t("JackfruitBananaBlossom.certifications.jas.description"),
        icon: "🇯🇵",
      },
      {
        name: t("JackfruitBananaBlossom.certifications.naturland.name"),
        image: "/images/certifications/Naturland.webp",
        alt: t("JackfruitBananaBlossom.certifications.naturland.name"),
        description: t(
          "JackfruitBananaBlossom.certifications.naturland.description"
        ),
        icon: "🌱",
      },
      {
        name: t("JackfruitBananaBlossom.certifications.fairtrade.name"),
        image: "/images/certifications/FairTrade.webp",
        alt: t("JackfruitBananaBlossom.certifications.fairtrade.name"),
        description: t(
          "JackfruitBananaBlossom.certifications.fairtrade.description"
        ),
        icon: "🤝",
      },
      {
        name: t("JackfruitBananaBlossom.certifications.fda.name"),
        image: "/images/certifications/FDA.webp",
        alt: t("JackfruitBananaBlossom.certifications.fda.name"),
        description: t("JackfruitBananaBlossom.certifications.fda.description"),
        icon: "✅",
      },
    ],
    [t]
  );

  // Common Features with translations
  const commonFeatures = useMemo(
    () => [
      {
        icon: <FaSeedling />,
        title: t("JackfruitBananaBlossom.features.plantBased.title"),
        desc: t("JackfruitBananaBlossom.features.plantBased.desc"),
      },
      {
        icon: <FaIcicles />,
        title: t("JackfruitBananaBlossom.features.gentleBrine.title"),
        desc: t("JackfruitBananaBlossom.features.gentleBrine.desc"),
      },
      {
        icon: <FaBoxOpen />,
        title: t("JackfruitBananaBlossom.features.multipleFormats.title"),
        desc: t("JackfruitBananaBlossom.features.multipleFormats.desc"),
      },
      {
        icon: <FaLeaf />,
        title: t("JackfruitBananaBlossom.features.cleanLabel.title"),
        desc: t("JackfruitBananaBlossom.features.cleanLabel.desc"),
      },
    ],
    [t]
  );

  // Applications with translations
  const applications = useMemo(
    () => [
      {
        icon: <FaFish />,
        text: t("JackfruitBananaBlossom.applications.seafood"),
      },
      {
        icon: <FaDrumstickBite />,
        text: t("JackfruitBananaBlossom.applications.meatAnalogues"),
      },
      {
        icon: <FaHamburger />,
        text: t("JackfruitBananaBlossom.applications.burgers"),
      },
      {
        icon: <FaPizzaSlice />,
        text: t("JackfruitBananaBlossom.applications.tacos"),
      },
      {
        icon: <FaUtensils />,
        text: t("JackfruitBananaBlossom.applications.curries"),
      },
      {
        icon: <FaPizzaSlice />,
        text: t("JackfruitBananaBlossom.applications.salads"),
      },
    ],
    [t]
  );

  // Cooking Methods with translations
  const cookingMethods = useMemo(
    () => [
      {
        icon: <FaFire />,
        method: t("JackfruitBananaBlossom.cookingMethods.braising.method"),
        desc: t("JackfruitBananaBlossom.cookingMethods.braising.desc"),
      },
      {
        icon: <FaUtensils />,
        method: t("JackfruitBananaBlossom.cookingMethods.sauteing.method"),
        desc: t("JackfruitBananaBlossom.cookingMethods.sauteing.desc"),
      },
      {
        icon: <FaFire />,
        method: t("JackfruitBananaBlossom.cookingMethods.grilling.method"),
        desc: t("JackfruitBananaBlossom.cookingMethods.grilling.desc"),
      },
      {
        icon: <FaUtensils />,
        method: t("JackfruitBananaBlossom.cookingMethods.baking.method"),
        desc: t("JackfruitBananaBlossom.cookingMethods.baking.desc"),
      },
      {
        icon: <FaFire />,
        method: t("JackfruitBananaBlossom.cookingMethods.deepFrying.method"),
        desc: t("JackfruitBananaBlossom.cookingMethods.deepFrying.desc"),
      },
    ],
    [t]
  );

  // Quality items with translations
  const qualityItems = useMemo(
    () => [
      {
        icon: <FaShieldAlt />,
        title: t("JackfruitBananaBlossom.quality.haccp.title"),
        desc: t("JackfruitBananaBlossom.quality.haccp.desc"),
      },
      {
        icon: <FaBarcode />,
        title: t("JackfruitBananaBlossom.quality.traceability.title"),
        desc: t("JackfruitBananaBlossom.quality.traceability.desc"),
      },
      {
        icon: <FaCertificate />,
        title: t("JackfruitBananaBlossom.quality.verified.title"),
        desc: t("JackfruitBananaBlossom.quality.verified.desc"),
      },
    ],
    [t]
  );

  // Usage items with translations
  const usageItems = useMemo(
    () => [
      {
        icon: <FaUtensils />,
        title: t("JackfruitBananaBlossom.usage.preparation.title"),
        text: t("JackfruitBananaBlossom.usage.preparation.text"),
      },
      {
        icon: <FaFire />,
        title: t("JackfruitBananaBlossom.usage.bestFor.title"),
        text: t("JackfruitBananaBlossom.usage.bestFor.text"),
      },
      {
        icon: <FaSnowflake />,
        title: t("JackfruitBananaBlossom.usage.afterOpening.title"),
        text: t("JackfruitBananaBlossom.usage.afterOpening.text"),
      },
    ],
    [t]
  );

  // Dietary benefits with translations
  const dietaryBenefits = useMemo(
    () => [
      {
        icon: <FaCarrot />,
        title: t("JackfruitBananaBlossom.dietaryBenefits.vegan.title"),
        desc: t("JackfruitBananaBlossom.dietaryBenefits.vegan.desc"),
      },
      {
        icon: <FaLeaf />,
        title: t("JackfruitBananaBlossom.dietaryBenefits.cleanLabel.title"),
        desc: t("JackfruitBananaBlossom.dietaryBenefits.cleanLabel.desc"),
      },
      {
        icon: <FaSeedling />,
        title: t("JackfruitBananaBlossom.dietaryBenefits.sustainable.title"),
        desc: t("JackfruitBananaBlossom.dietaryBenefits.sustainable.desc"),
      },
    ],
    [t]
  );

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Organic Jackfruit & Banana Blossom in Brine | Vegan Meat & Seafood Alternative"
        description="Premium organic young green jackfruit and banana blossom in gentle 1% brine. BRC certified, 100% vegan, ready-to-cook plant-based ingredients. Perfect for vegan fish, pulled pork, and meat alternatives. 24-month shelf life."
        keywords="organic jackfruit in brine, banana blossom vegan fish, young green jackfruit, vegan pulled pork, plant-based seafood, vegan meat alternative, organic jackfruit Sri Lanka, BRC certified jackfruit, banana blossom pieces, shredded banana blossom, pulled jackfruit, vegan poke, clean label vegan, sustainable plant-based, USDA organic jackfruit, EU organic banana blossom, Fairtrade jackfruit, vegan BBQ pulled pork, jackfruit tacos, banana blossom fish fry, organic meat substitute, ready-to-cook jackfruit"
        image="https://worganaturals.com/images/products/organic-jackfruit-banana-blossom-brine.webp"
        url="/products/jackfruit-banana-blossom"
        type="product"
        productSchema={true}
        category="Plant-Based Foods"
        brand="Worga Naturals"
        availability="InStock"
        price="4.99"
        currency="USD"
        sku="JBB-MIX-350"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Product Schema with Variants */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Organic Jackfruit & Banana Blossom in Brine",
            description:
              "Premium organic young green jackfruit and banana blossom in gentle 1% brine. BRC certified, 100% vegan, ready-to-cook plant-based ingredients with authentic meat-like texture. No added sugars, colors, flavors, or preservatives.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
            },
            image: [
              "https://worganaturals.com/images/products/organic-banana-blossom-pieces-brine.webp",
              "https://worganaturals.com/images/products/organic-banana-blossom-shredded-brine.webp",
              "https://worganaturals.com/images/products/organic-jackfruit-pieces-brine.webp",
              "https://worganaturals.com/images/products/organic-jackfruit-pulled-brine.webp",
            ],
            category: "Plant-Based Foods",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "4.99",
              highPrice: "24.99",
              offerCount: "4",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "89",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "24 months",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "350g glass jar, 400ml (A1) can, 3kg (A10) can, Custom",
              },
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value:
                  "BRC, EU Organic, USDA Organic, JAS, Naturland, Fairtrade, FDA Registered",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka",
              },
              {
                "@type": "PropertyValue",
                name: "Dietary",
                value: "100% Vegan, Plant-Based, Clean Label, No Added Sugars",
              },
              {
                "@type": "PropertyValue",
                name: "Brine Concentration",
                value: "1% gentle brine (salt and water only)",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Organic Banana Blossom Pieces in Brine (Vegan Fish)",
                description:
                  "100% organic banana blossom pieces in gentle 1% brine. Perfect for vegan fish fry, curries, tacos, tempura, and sandwiches. Soft texture with typical banana blossom flavor.",
                sku: "BBP-350",
                offers: {
                  "@type": "Offer",
                  price: "5.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft, yellowish pieces",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Best For",
                    value: "Vegan fish fry, curries, salads, tacos, tempura",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Banana Blossom Shredded in Brine",
                description:
                  "100% organic shredded banana blossom in gentle 1% brine. Ideal for vegan fish fillets, patties, stir-fries, and Sri Lankan mallung. Soft, shredable texture.",
                sku: "BBS-350",
                offers: {
                  "@type": "Offer",
                  price: "5.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft, shredable yellowish strands",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Best For",
                    value: "Vegan fish fillets, patties, stir-fries, mallung",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Young Green Jackfruit Pieces in Brine (Vegan Poke)",
                description:
                  "100% organic young green jackfruit pieces in gentle 1% brine. Perfect for vegan poke bowls, curries, stews, BBQ skewers, and fajitas. Soft, chunky texture.",
                sku: "JFP-350",
                offers: {
                  "@type": "Offer",
                  price: "6.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft, white chunky pieces",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Best For",
                    value: "Vegan poke bowls, curries, stews, BBQ skewers",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Pulled Young Green Jackfruit in Brine (Vegan Pulled Poke)",
                description:
                  "100% organic pulled young green jackfruit in gentle 1% brine. Ideal for vegan pulled pork, BBQ, sliders, tacos, biryani, and ramen toppings. Soft, meat-like pulled texture.",
                sku: "JFP-PULLED-350",
                offers: {
                  "@type": "Offer",
                  price: "6.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft, pulled meat-like white strands",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Best For",
                    value:
                      "Vegan pulled pork BBQ, sliders, tacos, biryani, ramen",
                  },
                ],
              },
            ],
            suitableForDiet: [
              "https://schema.org/VeganDiet",
              "https://schema.org/VegetarianDiet",
              "https://schema.org/GlutenFreeDiet",
            ],
            nutrition: {
              "@type": "NutritionInformation",
              servingSize: "100g",
              calories: "25 kcal",
              fatContent: "0.2g",
              carbohydrateContent: "5g",
              proteinContent: "1g",
              sodiumContent: "200mg",
            },
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Are there any additives in Worga Naturals Jackfruit & Banana Blossom?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No—100% organic, nothing added except gentle 1% brine (salt and water). No added sugars, colors, flavors, or preservatives.",
                },
              },
              {
                "@type": "Question",
                name: "Is it ready to eat?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "It's ready-to-cook. Drain, rinse if desired, season, and cook using your preferred method (fry, grill, braise, bake, or deep-fry).",
                },
              },
              {
                "@type": "Question",
                name: "Can I freeze jackfruit and banana blossom?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, you can freeze the contents after opening (transfer from glass jars to freezer-safe containers). Do not freeze unopened glass jars.",
                },
              },
              {
                "@type": "Question",
                name: "Is private label available for bulk orders?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—custom pack sizes, labels, and branding available for bulk orders. Contact us for private label inquiries.",
                },
              },
              {
                "@type": "Question",
                name: "What is the shelf life?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "24 months unopened. After opening, refrigerate and consume within 3–5 days.",
                },
              },
              {
                "@type": "Question",
                name: "What's the difference between pieces and pulled jackfruit?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Pieces have a chunkier texture ideal for curries and stews. Pulled/shredded has a fibrous, meat-like texture perfect for BBQ, tacos, and sandwiches.",
                },
              },
              {
                "@type": "Question",
                name: "How do I use banana blossom as vegan fish?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Drain and rinse the banana blossom pieces or shredded. Season with your favorite fish-style spices, coat with batter if desired, and fry, bake, or grill. Perfect for fish tacos, fish and chips, or curry.",
                },
              },
              {
                "@type": "Question",
                name: "Are these products certified organic?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—certified BRC, EU Organic, USDA Organic, JAS, Naturland, and Fairtrade. Full traceability with identity-preserved organic supply chain.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Jackfruit & Banana Blossom",
                item: "https://worganaturals.com/products/jackfruit-banana-blossom",
              },
            ],
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Premium organic products from Sri Lanka. Certified organic jackfruit, banana blossom, and plant-based ingredients.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-11-1234567",
              contactType: "Customer Service",
              email: "info@worganaturals.com",
              availableLanguage: ["en", "si"],
            },
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
          })}
        </script>

        {/* Recipe Schema - Example for Vegan Pulled Pork */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Recipe",
            name: "Vegan BBQ Pulled Pork with Organic Jackfruit",
            description:
              "Easy vegan pulled pork made with organic pulled jackfruit in brine. Perfect for BBQ sandwiches, sliders, and tacos.",
            image:
              "https://worganaturals.com/images/recipes/vegan-pulled-pork-jackfruit.webp",
            author: {
              "@type": "Organization",
              name: "Worga Naturals",
            },
            prepTime: "PT10M",
            cookTime: "PT20M",
            totalTime: "PT30M",
            recipeYield: "4 servings",
            recipeCategory: "Main Course",
            recipeCuisine: "American, Vegan",
            keywords:
              "vegan pulled pork, jackfruit BBQ, plant-based pulled pork",
            recipeIngredient: [
              "1 jar (350g) Organic Pulled Young Green Jackfruit in Brine",
              "1 cup BBQ sauce",
              "1 tbsp olive oil",
              "1 tsp smoked paprika",
              "1 tsp garlic powder",
              "1/2 tsp black pepper",
              "Burger buns or slider rolls",
            ],
            recipeInstructions: [
              {
                "@type": "HowToStep",
                text: "Drain and rinse the pulled jackfruit. Gently squeeze out excess moisture.",
              },
              {
                "@type": "HowToStep",
                text: "Heat olive oil in a large pan over medium heat. Add jackfruit and sauté for 5 minutes.",
              },
              {
                "@type": "HowToStep",
                text: "Add smoked paprika, garlic powder, and black pepper. Stir well.",
              },
              {
                "@type": "HowToStep",
                text: "Pour in BBQ sauce and mix thoroughly. Simmer for 15 minutes, stirring occasionally.",
              },
              {
                "@type": "HowToStep",
                text: "Serve hot on burger buns or slider rolls with coleslaw and pickles.",
              },
            ],
            suitableForDiet: [
              "https://schema.org/VeganDiet",
              "https://schema.org/VegetarianDiet",
            ],
          })}
        </script>

        {/* HowTo Schema - Cooking Methods */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Cook Organic Jackfruit & Banana Blossom",
            description:
              "Learn 5 cooking methods for organic jackfruit and banana blossom: braising, sautéing, grilling, baking, and deep-frying.",
            image:
              "https://worganaturals.com/images/howto/cooking-jackfruit-banana-blossom.webp",
            totalTime: "PT30M",
            step: [
              {
                "@type": "HowToStep",
                name: "Braising",
                text: "Slow-cook jackfruit or banana blossom in sauces (curry, tomato, BBQ) for 20-30 minutes for rich, deep flavor.",
              },
              {
                "@type": "HowToStep",
                name: "Sautéing",
                text: "Quick stir-fry with seasonings, garlic, and vegetables for 5-10 minutes over medium-high heat.",
              },
              {
                "@type": "HowToStep",
                name: "Grilling",
                text: "Marinate and grill on skewers or directly on grill for 10-15 minutes, turning occasionally for char marks.",
              },
              {
                "@type": "HowToStep",
                name: "Baking",
                text: "Toss with oil and spices, spread on baking sheet, and roast at 400°F (200°C) for 20-25 minutes until golden.",
              },
              {
                "@type": "HowToStep",
                name: "Deep-Frying",
                text: "Coat with batter (tempura, beer batter, or breadcrumbs) and deep-fry at 350°F (175°C) for 3-5 minutes until crispy.",
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-lime-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />
        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-green-600 to-lime-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("JackfruitBananaBlossom.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("JackfruitBananaBlossom.hero.title.part1")}{" "}
              </span>
              <span className="text-green-600 dark:text-green-500">
                {t("JackfruitBananaBlossom.hero.title.part2")}
              </span>
              <span className="text-gray-900 dark:text-white">
                {" "}
                {t("JackfruitBananaBlossom.hero.title.part3")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("JackfruitBananaBlossom.hero.description")}
            </p>

            {/* Traceability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg"
            >
              <FaBarcode className="text-green-600 text-2xl" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("JackfruitBananaBlossom.hero.traceability")}
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Common Features */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("JackfruitBananaBlossom.whyChoose.title")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {commonFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-green-600 mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Applications Section */}
            <div className="bg-gradient-to-r from-green-600 to-lime-600 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                {t("JackfruitBananaBlossom.idealFor.title")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {applications.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/30 transition-all duration-300"
                  >
                    <div className="text-3xl mb-2 flex justify-center">
                      {item.icon}
                    </div>
                    <p className="text-sm font-semibold">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Product Family */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("JackfruitBananaBlossom.productFamily.title")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setActiveProduct(index);
                    setActiveImageIndex(0);
                  }}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-green-600 to-lime-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  <span className="hidden lg:inline">{product.nickname}</span>
                  <span className="lg:hidden">
                    {product.image} {product.nickname}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <motion.div
              key={activeProduct}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2 gap-8">
                {/* Product Image Slider */}
                <div
                  className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                >
                  {/* Image Display */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeImageIndex}
                      initial={{ opacity: 0, scale: 0.9, y: 50 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -50 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={products[activeProduct].images[activeImageIndex]}
                        alt={products[activeProduct].name}
                        className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<div class="text-9xl md:text-[12rem]">${products[activeProduct].image}</div>`;
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation Arrows */}
                  {products[activeProduct].images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t(
                          "JackfruitBananaBlossom.navigation.previousImage"
                        )}
                      >
                        <FaChevronLeft className="text-green-600 text-2xl" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t(
                          "JackfruitBananaBlossom.navigation.nextImage"
                        )}
                      >
                        <FaChevronRight className="text-green-600 text-2xl" />
                      </button>
                    </>
                  )}

                  {/* Image Indicators */}
                  {products[activeProduct].images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2 z-10">
                      {products[activeProduct].images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveImageIndex(index)}
                          className={`transition-all duration-300 rounded-full ${
                            activeImageIndex === index
                              ? "bg-white w-10 h-3"
                              : "bg-white/50 hover:bg-white/75 w-3 h-3"
                          }`}
                          aria-label={t(
                            "JackfruitBananaBlossom.navigation.goToImage",
                            { number: index + 1 }
                          )}
                        />
                      ))}
                    </div>
                  )}

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold z-10">
                    {t("JackfruitBananaBlossom.navigation.imageCounter", {
                      current: activeImageIndex + 1,
                      total: products[activeProduct].images.length,
                    })}
                  </div>

                  {/* Decorative water droplets */}
                  <motion.div
                    animate={{
                      y: [0, 100],
                      opacity: [0.6, 0],
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                    className="absolute top-10 right-20 w-2 h-2 bg-white rounded-full"
                  />
                  <motion.div
                    animate={{
                      y: [0, 120],
                      opacity: [0.5, 0],
                    }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                    className="absolute top-20 right-32 w-3 h-3 bg-white rounded-full"
                  />
                  <motion.div
                    animate={{
                      y: [0, 90],
                      opacity: [0.7, 0],
                    }}
                    transition={{ duration: 1.8, repeat: Infinity, delay: 1 }}
                    className="absolute top-16 left-24 w-2 h-2 bg-white rounded-full"
                  />

                  {/* Additional decorative elements - Leaves */}
                  <motion.div
                    animate={{
                      y: [0, 110],
                      x: [0, -20],
                      opacity: [0.5, 0],
                      rotate: [0, 45],
                    }}
                    transition={{ duration: 3, repeat: Infinity, delay: 0.3 }}
                    className="absolute top-24 left-32 text-2xl opacity-40"
                  >
                    🍃
                  </motion.div>
                  <motion.div
                    animate={{
                      y: [0, 95],
                      x: [0, 15],
                      opacity: [0.6, 0],
                      rotate: [0, -30],
                    }}
                    transition={{ duration: 2.7, repeat: Infinity, delay: 0.8 }}
                    className="absolute top-32 right-28 text-xl opacity-30"
                  >
                    🌿
                  </motion.div>
                </div>

                {/* Product Details */}
                <div className="p-8 md:p-12">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {t("JackfruitBananaBlossom.productDetails.badge.inBrine")}
                    </span>
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      {t("JackfruitBananaBlossom.productDetails.badge.vegan")}
                    </span>
                  </div>

                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                    {products[activeProduct].name}
                  </h3>
                  <p className="text-xl text-green-600 dark:text-green-400 font-semibold mb-6">
                    "{products[activeProduct].nickname}"
                  </p>

                  {/* Sensory Profile */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <span className="font-bold text-green-600">
                        {t("JackfruitBananaBlossom.productDetails.appearance")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].appearance}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-green-600">
                        {t("JackfruitBananaBlossom.productDetails.aroma")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].aroma}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-green-600">
                        {t("JackfruitBananaBlossom.productDetails.flavour")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].flavour}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-green-600">
                        {t("JackfruitBananaBlossom.productDetails.texture")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].texture}
                      </span>
                    </div>
                  </div>

                  {/* Ingredients */}
                  <div className="bg-green-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                    <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">
                      {t("JackfruitBananaBlossom.productDetails.ingredients")}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      {products[activeProduct].ingredients}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      <strong>
                        {t("JackfruitBananaBlossom.productDetails.freeFrom")}:
                      </strong>{" "}
                      {t("JackfruitBananaBlossom.productDetails.freeFromText")}
                    </p>
                  </div>

                  {/* Serving Ideas */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      {t("JackfruitBananaBlossom.productDetails.servingIdeas")}:
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      {products[activeProduct].servingIdeas.map((idea, i) => (
                        <div
                          key={i}
                          className="flex items-center space-x-2 bg-gradient-to-r from-green-100 to-lime-100 dark:from-green-900/20 dark:to-lime-900/20 rounded-lg p-3"
                        >
                          <FaCheckCircle className="text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {idea}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Common Specs */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "JackfruitBananaBlossom.productDetails.specs.origin"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "JackfruitBananaBlossom.productDetails.specs.originValue"
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "JackfruitBananaBlossom.productDetails.specs.shelfLife"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "JackfruitBananaBlossom.productDetails.specs.shelfLifeValue"
                          )}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "JackfruitBananaBlossom.productDetails.specs.packSizes"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "JackfruitBananaBlossom.productDetails.specs.packSizesValue"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Process Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white shadow-xl"
            >
              <div className="flex items-start space-x-4">
                <FaRecycle className="text-5xl flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    {t("JackfruitBananaBlossom.process.title")}
                  </h3>
                  <p className="text-lg leading-relaxed">
                    {t("JackfruitBananaBlossom.process.description")}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Cooking Methods */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("JackfruitBananaBlossom.cookingMethods.title")}
              </h2>
              <div className="grid md:grid-cols-5 gap-6">
                {cookingMethods.map((method, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-green-50 dark:bg-gray-700 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300"
                  >
                    <div className="text-4xl text-green-600 mb-3 flex justify-center">
                      {method.icon}
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      {method.method}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {method.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("JackfruitBananaBlossom.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className = "text-2xl";
                          fallback.textContent = cert.icon;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality & Safety */}
        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("JackfruitBananaBlossom.quality.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {qualityItems.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="text-5xl text-green-600 mb-4 flex justify-center">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Usage & Storage */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              {t("JackfruitBananaBlossom.usage.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {usageItems.map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-5xl text-green-600 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Dietary Benefits */}
        <section className="bg-gradient-to-r from-purple-600 to-pink-600 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto text-center text-white"
            >
              <FaHeart className="text-6xl mb-6 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("JackfruitBananaBlossom.dietaryBenefits.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8 text-lg">
                {dietaryBenefits.map((benefit, i) => (
                  <div key={i}>
                    <div className="text-4xl mb-3 flex justify-center">
                      {benefit.icon}
                    </div>
                    <h3 className="font-bold mb-2">{benefit.title}</h3>
                    <p className="opacity-90">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQs */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("JackfruitBananaBlossom.faqsTitle")}
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-orange-50 dark:hover:bg-gray-700 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-4">
                      <FaQuestionCircle className="text-orange-600 text-2xl flex-shrink-0" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {faq.q}
                      </h3>
                    </div>
                    {openFaq === index ? (
                      <FaChevronUp className="text-orange-600 text-xl flex-shrink-0" />
                    ) : (
                      <FaChevronDown className="text-orange-600 text-xl flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed pl-10">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("JackfruitBananaBlossom.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("JackfruitBananaBlossom.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>
                  {t("JackfruitBananaBlossom.technical.downloadButton")}
                </span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default JackfruitBananaBlossom;
